import React from 'react';
import { flushAutosaves } from '../lib/autosaveRegistry';
import { readableErrorMessage } from '../utils/appErrors';

export type AutosaveStatusProps = {
  /** True while an async save is in flight */
  saving?: boolean;
  /** True when local state differs from last-saved state */
  dirty?: boolean;
  /** Error message from last save attempt */
  error?: string | null;
  /** Optional override label shown while a parent "closing" action runs */
  closingLabel?: string | null;
  /** Optional page-specific save action. Registered autosaves are flushed afterward. */
  onSave?: () => void | Promise<void>;
};

/**
 * Standardised save-status indicator for record headers.
 *
 * Renders the `.record-header__status` markup with a coloured dot and label:
 *  - Green dot + "Saved"          (default)
 *  - Amber pulsing dot + "Saving…" (saving=true)
 *  - Amber static dot + "Unsaved changes" (dirty=true, saving=false)
 *  - Red dot + error text          (error is set)
 *
 * Clicking the indicator immediately flushes registered autosaves app-wide.
 * Drop this into RecordHeader's `aside` prop alongside nav buttons, etc.
 */
export default function AutosaveStatus({
  saving = false,
  dirty = false,
  error = null,
  closingLabel = null,
  onSave,
}: AutosaveStatusProps) {
  const [manualSaving, setManualSaving] = React.useState(false);
  const [manualError, setManualError] = React.useState<string | null>(null);
  const reasonId = React.useId();
  // The owning autosave state clears its own error after a successful retry.
  // Do not retain a duplicate manual error after that owner has taken over.
  React.useEffect(() => { if (error) setManualError(null); }, [error]);
  const effectiveSaving = saving || manualSaving;
  const effectiveError = error || manualError
    ? readableErrorMessage(error || manualError, 'Your changes could not be saved. Try again. If it still fails, contact support.')
    : null;
  const showSaving = effectiveSaving || !!closingLabel;
  const showDirty = !effectiveSaving && !closingLabel && dirty && !effectiveError;

  const saveNow = React.useCallback(async () => {
    if (effectiveSaving || closingLabel) return;
    setManualSaving(true);
    setManualError(null);
    try {
      await onSave?.();
      await flushAutosaves();
    } catch (saveError) {
      setManualError(readableErrorMessage(saveError, 'Your changes could not be saved. Try again. If it still fails, contact support.'));
    } finally {
      setManualSaving(false);
    }
  }, [closingLabel, effectiveSaving, onSave]);

  let text: string;
  if (closingLabel) {
    text = closingLabel;
  } else if (effectiveError) {
    text = 'Save error';
  } else if (effectiveSaving) {
    text = 'Saving\u2026';
  } else if (dirty) {
    text = 'Unsaved changes';
  } else {
    text = 'Saved';
  }

  return (
    <span className="autosave-status">
      <button
        type="button"
        className="record-header__status record-header__status--action"
        onClick={() => { void saveNow(); }}
        disabled={effectiveSaving || Boolean(closingLabel)}
        aria-describedby={effectiveError ? reasonId : undefined}
        aria-label={
          effectiveSaving || closingLabel
            ? text
            : effectiveError
              ? 'Retry saving changes'
              : dirty
                ? 'Save changes now'
                : 'All changes saved. Save again'
        }
        title={effectiveSaving || closingLabel ? text : effectiveError ? 'Retry saving changes' : 'Save changes now'}
      >
        <span
          className="record-header__status-dot"
          data-saving={showSaving || undefined}
          data-dirty={showDirty || undefined}
          data-error={!!effectiveError || undefined}
        />
        <span className="record-header__status-text">{text}</span>
      </button>
      {effectiveError ? (
        <span id={reasonId} className="autosave-status__reason" role="alert">
          {effectiveError} <span className="autosave-status__retry-hint">Select Save error to retry.</span>
        </span>
      ) : null}
    </span>
  );
}
